/**
 * Keyboard shortcuts (#93), kept on the control they press.
 *
 * A shortcut is written on its button as `aria-keyshortcuts` — the ARIA attribute that tells a
 * screen reader the same thing — and nowhere else. The button draws it beside its label; the shell
 * presses the one button on the page whose shortcut matches a key; the `?` sheet lists every one
 * the page has. One source, so the sheet, the hint and the key can never disagree, and a unit
 * gives its hand-written editor a shortcut by setting one attribute on its own button.
 *
 * A manifest names an action's shortcut as `hotkey`, in a short form: `n`, `shift+n`, `mod+s`,
 * `mod+enter`, `alt+n` — `mod` is ⌘ on a Mac and Ctrl elsewhere, and is written as both
 * alternatives (`Control+S Meta+S`) so either machine matches.
 */
const MODIFIERS = { mod: 'mod', ctrl: 'Control', control: 'Control', meta: 'Meta', alt: 'Alt', shift: 'Shift' };
const KEYS = { enter: 'Enter', escape: 'Escape', esc: 'Escape', space: 'Space', '?': '?', '/': '/' };
/** The ARIA form of a manifest's `hotkey`: `mod+s` → `Control+S Meta+S`, `n` → `N`. Null for one this cannot read. */
export function ariaKeyshortcuts(spec) {
    if (!spec)
        return null;
    const parts = spec.toLowerCase().split('+').map((p) => p.trim()).filter(Boolean);
    if (!parts.length)
        return null;
    const last = parts[parts.length - 1];
    const key = KEYS[last] ?? (/^[a-z0-9]$/.test(last) ? last.toUpperCase() : null);
    if (!key)
        return null;
    const mods = parts.slice(0, -1).map((m) => MODIFIERS[m]);
    if (mods.some((m) => !m))
        return null;
    const order = ['Control', 'Meta', 'Alt', 'Shift'];
    const write = (ms) => [...[...new Set(ms)].sort((a, b) => order.indexOf(a) - order.indexOf(b)), key].join('+');
    if (mods.includes('mod')) {
        const rest = mods.filter((m) => m !== 'mod');
        return `${write(['Control', ...rest])} ${write(['Meta', ...rest])}`;
    }
    return write(mods);
}
/**
 * Whether a key press is one of the alternatives an `aria-keyshortcuts` names. A letter or a digit
 * is matched on the key's place (`KeyN`), since Alt turns `n` into `˜` on a Mac; the modifiers must
 * be exactly those written. `?` is matched as the character, whatever Shift it took to type it.
 */
export function matchesKeyshortcuts(e, aria) {
    if (!aria)
        return false;
    return aria.split(/\s+/).filter(Boolean).some((alt) => {
        const parts = alt.split('+');
        const key = parts[parts.length - 1];
        const mods = new Set(parts.slice(0, -1));
        if (key === '?')
            return (e.key === '?' || e.key === '؟') && !e.ctrlKey && !e.metaKey && !e.altKey;
        const pressed = /^[A-Z]$/.test(key) ? e.code === `Key${key}` || e.key.toUpperCase() === key
            : /^[0-9]$/.test(key) ? e.code === `Digit${key}` || e.key === key
                : key === 'Space' ? e.key === ' ' : e.key === key;
        return pressed && e.ctrlKey === mods.has('Control') && e.metaKey === mods.has('Meta')
            && e.altKey === mods.has('Alt') && e.shiftKey === mods.has('Shift');
    });
}
/** The shortcut as a person reads it on this machine: `⌘S` on a Mac, `Ctrl+S` elsewhere. */
export function keyshortcutText(aria, mac) {
    if (!aria)
        return '';
    const alternatives = aria.split(/\s+/).filter(Boolean);
    // Of `Control+S Meta+S`, the one this machine uses.
    const chosen = alternatives.find((a) => (mac ? !a.split('+').includes('Control') : !a.split('+').includes('Meta'))) ?? alternatives[0];
    const parts = chosen.split('+');
    const glyph = mac
        ? { Control: '⌃', Meta: '⌘', Alt: '⌥', Shift: '⇧', Enter: '↵' }
        : { Control: 'Ctrl', Meta: 'Win', Alt: 'Alt', Shift: 'Shift', Enter: 'Enter' };
    const words = parts.map((p) => glyph[p] ?? p);
    return mac ? words.join('') : words.join('+');
}
export function isMacPlatform() {
    return typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent || '');
}
/** Whether a key is being typed into a field — a plain key there is text, not a shortcut. */
export function isTyping(target) {
    const el = target;
    return Boolean(el?.closest?.('input,textarea,select,[contenteditable="true"],[role="combobox"]'));
}
/** Whether a press has a modifier that makes it a command even inside a field (⌘S, Ctrl+Enter, Alt+N). */
export function isCommand(e) {
    return e.ctrlKey || e.metaKey || e.altKey;
}
/** The shortcut a list's primary action has when its manifest names none: `N`, for new. */
export const PRIMARY_HOTKEY = 'n';
//# sourceMappingURL=hotkeys.js.map